import { useEffect, useRef, useState } from "react"

import { DelayStepper } from "./DelayStepper"
import { PlusIcon, XIcon } from "../../../../shared/components"
import { PauseIcon, PlayIcon } from "../../../components/icons"
import { ACCEPT } from "../../../config"

import type { Frame } from "../../../types/Frame"
import type { Playback } from "../../../types/Playback"
import type { ReactNode } from "react"

// Room for what sits outside a tile: its focus ring and its remove button.
const REVEAL_MARGIN_PX = 8

// Moves only the ribbon: scrollIntoView would also pull the page to the strip.
const revealTile = (ribbon: HTMLElement | null, index: number) => {
  const tile = ribbon?.children[index]
  if (!ribbon || !tile) return

  const box = ribbon.getBoundingClientRect()
  const rect = tile.getBoundingClientRect()
  const before = box.left + REVEAL_MARGIN_PX - rect.left
  const after = rect.right - (box.right - REVEAL_MARGIN_PX)
  if (before > 0) ribbon.scrollLeft -= before
  else if (after > 0) ribbon.scrollLeft += after
}

interface Props {
  frames: Frame[]
  activeIndex: number
  isPlaying: boolean
  isAddingFrames: boolean
  isLocked: boolean
  delayMs: number
  loopMs: number
  playback: Playback
  onPlayToggle: () => void
  onFrameSelect: (index: number) => void
  onFramesChange: (frames: Frame[]) => void
  onFramesAdd: (files: File[]) => void
  onDelayChange: (delayMs: number) => void
  onPlaybackChange: (playback: Playback) => void
}

export const AnimationStrip = ({
  frames,
  activeIndex,
  isPlaying,
  isAddingFrames,
  isLocked,
  delayMs,
  loopMs,
  playback,
  onPlayToggle,
  onFrameSelect,
  onFramesChange,
  onFramesAdd,
  onDelayChange,
  onPlaybackChange,
}: Props) => {
  const ribbonRef = useRef<HTMLDivElement>(null)
  const addInputRef = useRef<HTMLInputElement>(null)
  const [dragIndex, setDragIndex] = useState<number | null>(null)
  const [dropIndex, setDropIndex] = useState<number | null>(null)

  // Attached natively: React's wheel listener is passive, so it can't keep the page from scrolling.
  useEffect(() => {
    const ribbon = ribbonRef.current
    if (!ribbon) return

    const handleWheel = (event: WheelEvent) => {
      if (ribbon.scrollWidth <= ribbon.clientWidth) return
      if (Math.abs(event.deltaY) <= Math.abs(event.deltaX)) return
      // At either end the wheel goes back to scrolling the page.
      const max = ribbon.scrollWidth - ribbon.clientWidth
      if (event.deltaY < 0 ? ribbon.scrollLeft <= 0 : ribbon.scrollLeft >= max - 1) return

      event.preventDefault()
      ribbon.scrollLeft += event.deltaY
    }
    ribbon.addEventListener("wheel", handleWheel, { passive: false })
    return () => ribbon.removeEventListener("wheel", handleWheel)
  }, [])

  const moveFrame = (from: number, to: number) => {
    if (from === to) return

    const moved = [...frames]
    moved.splice(to, 0, ...moved.splice(from, 1))
    onFramesChange(moved)
  }

  const endDrag = () => {
    setDragIndex(null)
    setDropIndex(null)
  }

  // One decimal below 10: rounded to a whole number, 2000 ms would read as 1 fps instead of 0.5.
  const fps = 1000 / delayMs
  const fpsLabel = fps < 10 ? Number(fps.toFixed(1)) : Math.round(fps)

  // A frame dragged forward lands after its target, one dragged backward lands before it.
  const dropShift = (dropIndex ?? 0) > (dragIndex ?? 0) ? "-translate-x-1.5" : "translate-x-1.5"

  return (
    <section
      aria-label="Animation"
      inert={isLocked}
      className={`flex flex-col gap-3 rounded-box bg-base-300 p-3 transition-opacity ${
        isLocked ? "opacity-60" : ""
      }`}
    >
      <div className="flex items-center gap-2">
        <button
          type="button"
          aria-label={isPlaying ? "Pause" : "Play"}
          onClick={onPlayToggle}
          className="btn btn-square rounded-lg btn-soft btn-primary"
        >
          {isPlaying ? <PauseIcon className="size-4.5" /> : <PlayIcon className="size-4.5" />}
        </button>
        <div
          ref={ribbonRef}
          className="flex min-w-0 flex-1 [scrollbar-width:thin] gap-2 overflow-x-auto px-1 py-2"
        >
          {frames.map((frame, index) => (
            // Only a frame drag is handled here; dragged files fall through to the card's dropzone.
            <div
              key={frame.id}
              draggable
              onDragStart={(event) => {
                event.dataTransfer.effectAllowed = "move"
                event.dataTransfer.setData("text/plain", String(index))
                setDragIndex(index)
                // Pauses on the dragged frame: a playing preview re-renders the tiles mid-drag.
                onFrameSelect(index)
              }}
              onDragOver={(event) => {
                if (dragIndex === null) return

                event.preventDefault()
                setDropIndex(index)
              }}
              onDrop={(event) => {
                if (dragIndex === null) return

                event.preventDefault()
                moveFrame(dragIndex, index)
                endDrag()
              }}
              onDragEnd={endDrag}
              className={`group relative size-12 shrink-0 cursor-grab rounded-lg border-2 transition bg-checkerboard has-focus-visible:ring-3 has-focus-visible:ring-primary/60 ${
                index === activeIndex
                  ? "border-primary"
                  : "border-transparent hover:border-base-content/25"
              } ${index === dragIndex ? "opacity-40" : ""} ${
                index === dropIndex && index !== dragIndex ? dropShift : ""
              }`}
            >
              <button
                type="button"
                aria-label={`Frame ${index + 1}`}
                title={frame.name}
                aria-pressed={!isPlaying && index === activeIndex}
                onClick={() => onFrameSelect(index)}
                onFocus={() => revealTile(ribbonRef.current, index)}
                className="block size-full cursor-grab overflow-hidden rounded-md outline-none"
              >
                {/* Not draggable itself: a dragged <img> carries a file, which the dropzone would take as a new source. */}
                <img
                  src={frame.url}
                  alt=""
                  draggable={false}
                  className="pointer-events-none block size-full [image-rendering:pixelated]"
                />
              </button>
              <button
                type="button"
                aria-label={`Remove frame ${index + 1}`}
                onClick={(event) => {
                  // The neighbour's node outlives the re-render, so focus doesn't fall to <body>.
                  const tile = event.currentTarget.parentElement
                  const isLast = index === frames.length - 1
                  const neighbour = isLast ? tile?.previousElementSibling : tile?.nextElementSibling
                  neighbour?.querySelector("button")?.focus()
                  onFramesChange(frames.filter((other) => other !== frame))
                }}
                className="invisible absolute -top-2 -right-2 grid size-4 cursor-pointer place-items-center rounded-full bg-error text-error-content ring-2 ring-base-300 group-focus-within:visible group-hover:visible"
              >
                <XIcon className="size-2.5" />
              </button>
            </div>
          ))}
          {/* aria-disabled, not disabled: a disabled button drops keyboard focus to <body>. */}
          <button
            type="button"
            aria-label="Add frames"
            title="Add frames"
            aria-disabled={isAddingFrames}
            onClick={() => {
              if (!isAddingFrames) addInputRef.current?.click()
            }}
            className="grid size-12 shrink-0 cursor-pointer place-items-center rounded-lg border-2 border-dashed border-base-content/25 text-muted-content hover:border-primary hover:text-primary"
          >
            {isAddingFrames ? (
              <span className="loading loading-xs loading-spinner" aria-hidden="true" />
            ) : (
              <PlusIcon />
            )}
          </button>
          <input
            ref={addInputRef}
            type="file"
            multiple
            hidden
            accept={ACCEPT}
            onChange={(event) => {
              const files = Array.from(event.currentTarget.files ?? [])
              event.currentTarget.value = ""
              if (files.length > 0) onFramesAdd(files)
            }}
          />
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <p className="mr-auto flex items-baseline gap-2 text-sm font-bold whitespace-nowrap">
          Animation
          <span className="text-xs font-semibold text-muted-content tabular-nums">
            {frames.length} frames · <b className="text-base-content">{fpsLabel} fps</b> ·{" "}
            {(loopMs / 1000).toFixed(1)} s
          </span>
        </p>
        <DelayStepper delayMs={delayMs} onChange={onDelayChange} />
        <div role="group" aria-label="Playback" className="flex flex-wrap gap-1">
          <PlaybackButton pressed={playback === "loop"} onClick={() => onPlaybackChange("loop")}>
            Loop
          </PlaybackButton>
          <PlaybackButton
            pressed={playback === "pingpong"}
            onClick={() => onPlaybackChange("pingpong")}
          >
            Ping-pong
          </PlaybackButton>
          <PlaybackButton
            pressed={playback === "reverse"}
            onClick={() => onPlaybackChange("reverse")}
          >
            Reverse
          </PlaybackButton>
        </div>
      </div>
    </section>
  )
}

const PlaybackButton = ({
  pressed,
  onClick,
  children,
}: {
  pressed: boolean
  onClick: () => void
  children: ReactNode
}) => (
  <button
    type="button"
    aria-pressed={pressed}
    onClick={onClick}
    className={`btn rounded-full font-bold btn-sm ${
      pressed ? "btn-soft btn-primary" : "border-base-content/10 bg-base-200 text-muted-content"
    }`}
  >
    {children}
  </button>
)
